import type React from "react";
import {
  type FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BottomSheet as BottomSheetComponent,
  type BottomSheetRef,
} from "react-spring-bottom-sheet";
import "./style.css";

interface BottomSheetProps {
  description?: string;
  homepage?: string;
  meta?: unknown;
  options: {
    openTo: "top" | "middle" | "bottom";
    positions: {
      bottom?: number;
      middle?: number;
      top?: number;
    };
    expandOnContentDrag: boolean;
    showHandle: boolean;
    open?: boolean;
  };
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
  setOpen?: (state: boolean) => void;
  withSpecialHeader?: boolean;
  shouldSnapToTop?: boolean;
  onPositionChange?: (direction: "draggingUp" | "draggingDown") => void;
  lofiMode?: boolean;
}

// Enhanced lofi mode component with drag functionality but no animations
const LofiBottomSheet: FC<{
  options: BottomSheetProps["options"];
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
  setOpen?: (state: boolean) => void;
  onPositionChange?: (direction: "draggingUp" | "draggingDown") => void;
  withSpecialHeader?: boolean;
}> = ({
  options,
  header,
  footer,
  children,
  open,
  setOpen: _setOpen,
  onPositionChange,
  withSpecialHeader = false,
}) => {
  const isOpen = Boolean(options.open || open);
  const [currentPosition, setCurrentPosition] = useState<"bottom" | "top">(
    options.openTo === "middle" ? "bottom" : options.openTo,
  );
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartHeight, setDragStartHeight] = useState(0);
  const [lastDeltaY, setLastDeltaY] = useState(0);
  const [initialTransform, setInitialTransform] = useState("translateY(100%)");
  const sheetRef = useRef<HTMLDivElement>(null);
  const isSnappingRef = useRef(false);

  // Calculate positions based on window height - only bottom and top
  const positions = useMemo(() => {
    const windowHeight = window.innerHeight;
    return {
      bottom: options.positions.bottom || Math.min(200, windowHeight * 0.3),
      top: options.positions.top || windowHeight - 100,
    };
  }, [options.positions]);

  // Check if dragging should be enabled (both positions must be defined and different)
  const isDragEnabled = useMemo(() => {
    return (
      options.positions.bottom !== undefined &&
      options.positions.top !== undefined &&
      options.positions.bottom !== options.positions.top
    );
  }, [options.positions.bottom, options.positions.top]);

  const getCurrentHeight = useCallback(() => {
    switch (currentPosition) {
      case "top":
        return positions.top;
      default:
        return positions.bottom;
    }
  }, [currentPosition, positions]);

  const snapToPosition = useCallback(
    (position: "bottom" | "top") => {
      isSnappingRef.current = true;

      if (sheetRef.current) {
        const targetHeight =
          position === "top" ? positions.top : positions.bottom;

        sheetRef.current.style.height = `${targetHeight}px`;

        // Set position immediately since useEffect is now blocked by ref
        setCurrentPosition(position);

        // Clear snapping flag after DOM has time to update
        setTimeout(() => {
          isSnappingRef.current = false;
        }, 100);
      }
    },
    [positions],
  );

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!options.showHandle || !isDragEnabled) return;
    setIsDragging(true);
    setDragStartY(e.clientY);
    setDragStartHeight(getCurrentHeight());
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!options.showHandle || !isDragEnabled) return;
    setIsDragging(true);
    setDragStartY(e.touches[0].clientY);
    setDragStartHeight(getCurrentHeight());
  };

  const handleDrag = useCallback(
    (clientY: number) => {
      if (!isDragging || !sheetRef.current) return;

      const deltaY = dragStartY - clientY;
      setLastDeltaY(deltaY);

      const newHeight = Math.max(
        100,
        Math.min(window.innerHeight - 50, dragStartHeight + deltaY),
      );

      sheetRef.current.style.height = `${newHeight}px`;

      // Determine direction for callback
      if (Math.abs(deltaY) > 10) {
        const direction = deltaY > 0 ? "draggingUp" : "draggingDown";
        onPositionChange?.(direction);
      }
    },
    [isDragging, dragStartY, dragStartHeight, onPositionChange],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      handleDrag(e.clientY);
    },
    [handleDrag],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      handleDrag(e.touches[0].clientY);
    },
    [handleDrag],
  );

  const handleDragEnd = useCallback(() => {
    if (!isDragging || !sheetRef.current) return;

    setIsDragging(false);

    // Use actual mouse/touch movement instead of constrained height difference
    const dragDistance = lastDeltaY;

    // Simple 50px threshold in both directions
    let targetPosition: "bottom" | "top";
    if (Math.abs(dragDistance) < 50) {
      // Not enough movement, stay in current position
      targetPosition = currentPosition;
    } else if (dragDistance > 0) {
      // Dragged up 50px or more, go to top
      targetPosition = "top";
    } else {
      // Dragged down 50px or more, go to bottom
      targetPosition = "bottom";
    }

    // Force immediate snap to prevent getting stuck
    setTimeout(() => {
      snapToPosition(targetPosition);
    }, 0);
  }, [isDragging, currentPosition, lastDeltaY, snapToPosition]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleDragEnd);
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleDragEnd);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleDragEnd);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleDragEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleTouchMove, handleDragEnd]);

  // Handle opening animation
  useEffect(() => {
    if (isOpen) {
      // Start from below screen
      setInitialTransform("translateY(100%)");
      // Animate to final position
      setTimeout(() => {
        setInitialTransform("translateY(0)");
      }, 10);
    } else {
      // Reset when closed
      setInitialTransform("translateY(100%)");
    }
  }, [isOpen]);

  // React to openTo changes (but not during snapping)
  useEffect(() => {
    if (!isSnappingRef.current) {
      const newPosition =
        options.openTo === "middle" ? "bottom" : options.openTo;
      setCurrentPosition(newPosition);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.openTo]);

  useEffect(() => {
    if (sheetRef.current && !isSnappingRef.current) {
      const targetHeight = getCurrentHeight();
      sheetRef.current.style.height = `${targetHeight}px`;

      // Fallback: ensure we're never stuck in an invalid position
      const currentHeight = sheetRef.current.offsetHeight;
      const midpoint = (positions.bottom + positions.top) / 2;

      if (currentHeight > positions.bottom && currentHeight < positions.top) {
        // If we're in the middle range, snap to the appropriate position
        const correctPosition = currentHeight < midpoint ? "bottom" : "top";
        setCurrentPosition(correctPosition);
        // Force immediate height correction
        const correctHeight =
          correctPosition === "top" ? positions.top : positions.bottom;
        sheetRef.current.style.height = `${correctHeight}px`;
      }
    }
  }, [getCurrentHeight, positions]);

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 bottom-0 left-0 z-50">
      {/* Outer container with transform animation */}
      <div
        style={{
          transform: initialTransform,
          transition: "transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        }}
      >
        {/* Sheet */}
        <div
          ref={sheetRef}
          className="bg-white flex flex-col"
          style={{
            height: getCurrentHeight(),
            transition: isDragging ? "none" : "height 0.2s ease-out",
            borderTopLeftRadius: currentPosition === "top" ? "16px" : "16px",
            borderTopRightRadius: currentPosition === "top" ? "16px" : "16px",
            boxShadow:
              "0 -5px 60px 0 rgba(38, 89, 115, 0.11), 0 -1px 0 rgba(38, 89, 115, 0.05)",
          }}
        >
          {/* Header with Handle - only show if showHandle is true */}
          {options.showHandle && isDragEnabled && (
            <button
              type="button"
              className="flex-shrink-0 text-center select-none relative w-full"
              style={{
                padding: "16px",
                paddingTop: "calc(20px + env(safe-area-inset-top))",
                paddingBottom: "8px",
                zIndex: 1,
                cursor: isDragEnabled ? "ns-resize" : "default",
                pointerEvents: isDragEnabled ? "all" : "none",
                backgroundColor: "transparent",
                border: "none",
              }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              disabled={!isDragEnabled}
              aria-label={
                isDragEnabled ? "Drag to resize bottom sheet" : "Bottom sheet"
              }
            >
              {/* Handle */}
              {isDragEnabled && (
                <div
                  className="absolute left-1/2 transform -translate-x-1/2 w-9 h-1 rounded-sm"
                  style={{
                    top: "calc(8px + env(safe-area-inset-top))",
                    backgroundColor: "hsla(0, 0%, 0%, 0.14)",
                  }}
                />
              )}

              {/* Header Content */}
              {withSpecialHeader ? (
                <div className="">
                  {header}
                  <div
                    className={`h-48 absolute top-0 left-0 right-0 z-10 transition-opacity duration-200 ${
                      currentPosition === "bottom" ? "visible" : "hidden"
                    }`}
                  />
                </div>
              ) : (
                header
              )}
            </button>
          )}

          {/* Content */}
          <div
            className="flex-shrink flex-grow overflow-auto"
            style={{
              paddingBottom: footer ? "0" : "env(safe-area-inset-bottom)",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
            }}
          >
            <div className="overflow-hidden">{children}</div>
          </div>

          {/* Footer */}
          {footer && (
            <div
              className="flex-shrink-0"
              style={{
                boxShadow:
                  "0px -7px 15px -3px rgb(255, 255, 255, 0.5), 0px -5px 15px -3px hsla(37, 100%, 30%, 0.15)",
                borderTop: "1px solid hsla(44, 12%, 91%, 1)",
                overflow: "hidden",
                zIndex: 10,
                padding: "16px",
                paddingBottom: "calc(16px + env(safe-area-inset-bottom))",
              }}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Full-featured BottomSheet component
const FullBottomSheet: FC<BottomSheetProps> = ({
  options,
  header,
  footer,
  children,
  open,
  setOpen,
  withSpecialHeader = false,
  shouldSnapToTop = false,
  onPositionChange,
}) => {
  // Full-featured mode (existing implementation)
  const [, setIsAtTop] = useState(true);
  const [currentPosition, setCurrentPosition] = useState<
    "bottom" | "middle" | "top"
  >(options.openTo);

  // Memoize positions to avoid recalculation
  const positions = useMemo(() => {
    const defaultPositions = {
      bottom: 100,
      middle: 400,
      top: window.innerHeight - 100,
    };
    return {
      bottom: options.positions.bottom || defaultPositions.bottom,
      middle: options.positions.middle || defaultPositions.middle,
      top: options.positions.top || defaultPositions.top,
    };
  }, [options.positions]);

  const {
    bottom: bottomPosition,
    middle: middlePosition,
    top: topPosition,
  } = positions;
  const sheetRef = useRef<BottomSheetRef>(null);
  const position = options.open || options.openTo;

  const [scrollPosition, setScrollPosition] = useState(0);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [hasBeenAtTopLongEnough, setHasBeenAtTopLongEnough] = useState(false);
  const scrollAtTopTimerRef = useRef<NodeJS.Timeout | null>(null);
  const dragTrackingRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const lastDirectionRef = useRef<"draggingUp" | "draggingDown" | null>(null);
  const verificationTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (sheetRef.current) {
      switch (position) {
        case "top":
          sheetRef.current.snapTo(topPosition, { source: "snap-to-top" });
          break;
        case "middle":
          sheetRef.current.snapTo(middlePosition, { source: "snap-to-middle" });
          break;
        default:
          sheetRef.current.snapTo(bottomPosition, { source: "snap-to-bottom" });
          break;
      }
    }
  }, [position, bottomPosition, middlePosition, topPosition]);

  const onDismiss = useCallback(() => {
    setOpen?.(false);
  }, [setOpen]);

  const onSpringEnd = useCallback(
    (_event: unknown) => {
      const height = sheetRef.current?.height || 0;

      // Stop drag tracking
      isDraggingRef.current = false;
      dragTrackingRef.current = null;
      lastDirectionRef.current = null;

      // Clear verification timer
      if (verificationTimerRef.current) {
        clearTimeout(verificationTimerRef.current);
        verificationTimerRef.current = null;
      }

      if (height <= bottomPosition + 50) {
        setCurrentPosition("bottom");
        setIsAtTop(false);
      } else if (height <= middlePosition + 50) {
        setCurrentPosition("middle");
        setIsAtTop(false);
      } else {
        setCurrentPosition("top");
        setIsAtTop(true);
      }
    },
    [bottomPosition, middlePosition],
  );

  // Throttled scroll handler for better performance
  const throttledScrollHandler = useCallback(() => {
    const ideaMapList = document.getElementById("ideaMapList");
    const container =
      ideaMapList || document.querySelector("[data-rsbs-scroll]");

    if (container instanceof HTMLElement) {
      const newScrollPosition = container.scrollTop;
      setScrollPosition(newScrollPosition);
      const isAtTopNow = newScrollPosition === 0;
      setIsAtTop(isAtTopNow);

      // Clear any existing timer
      if (scrollAtTopTimerRef.current) {
        clearTimeout(scrollAtTopTimerRef.current);
        scrollAtTopTimerRef.current = null;
      }

      // If we're at the top, start a timer
      if (isAtTopNow) {
        setHasBeenAtTopLongEnough(false);
        scrollAtTopTimerRef.current = setTimeout(() => {
          setHasBeenAtTopLongEnough(true);
        }, 150);
      } else {
        setHasBeenAtTopLongEnough(false);
      }
    }
  }, []);

  useEffect(() => {
    const sheet = sheetRef.current;
    if (sheet) {
      const ideaMapList = document.getElementById("ideaMapList");
      const container =
        ideaMapList || document.querySelector("[data-rsbs-scroll]");

      if (container instanceof HTMLElement) {
        // Run initial check
        throttledScrollHandler();

        // Throttle scroll events for better performance
        let scrollTimeout: NodeJS.Timeout;
        const handleScroll = () => {
          if (scrollTimeout) clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(throttledScrollHandler, 16); // ~60fps
        };

        container.addEventListener("scroll", handleScroll, { passive: true });
        return () => {
          if (scrollTimeout) clearTimeout(scrollTimeout);
          if (scrollAtTopTimerRef.current) {
            clearTimeout(scrollAtTopTimerRef.current);
            scrollAtTopTimerRef.current = null;
          }
          container.removeEventListener("scroll", handleScroll);
        };
      }
    }
  }, [throttledScrollHandler]);

  const handleTouchStart = useCallback((event: Event) => {
    const touchEvent = event as TouchEvent;
    const touch = touchEvent.touches[0];
    setTouchStartY(touch.clientY);
  }, []);

  const touchMoveHandler = useCallback(
    (event: Event) => {
      const touchEvent = event as TouchEvent;
      if (
        touchStartY !== null &&
        currentPosition !== "bottom" &&
        hasBeenAtTopLongEnough &&
        scrollPosition === 0
      ) {
        const touch = touchEvent.touches[0];
        const deltaY = touch.clientY - touchStartY;

        if (deltaY > 50 && sheetRef.current) {
          sheetRef.current.snapTo(bottomPosition, { source: "snap-to-bottom" });
          setTouchStartY(null);
          setHasBeenAtTopLongEnough(false);
        }
      }
    },
    [
      touchStartY,
      currentPosition,
      hasBeenAtTopLongEnough,
      scrollPosition,
      bottomPosition,
    ],
  );

  const handleTouchEnd = useCallback(() => {
    setTouchStartY(null);
  }, []);

  useEffect(() => {
    const element = document.querySelector("[data-rsbs-scroll]");
    if (element) {
      element.addEventListener(
        "touchstart",
        handleTouchStart as EventListener,
        { passive: true },
      );
      element.addEventListener("touchmove", touchMoveHandler as EventListener, {
        passive: true,
      });
      element.addEventListener("touchend", handleTouchEnd, { passive: true });

      return () => {
        element.removeEventListener(
          "touchstart",
          handleTouchStart as EventListener,
        );
        element.removeEventListener(
          "touchmove",
          touchMoveHandler as EventListener,
        );
        element.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [handleTouchStart, touchMoveHandler, handleTouchEnd]);

  useEffect(() => {
    if (!shouldSnapToTop || !sheetRef.current) return;
    sheetRef.current?.snapTo(topPosition, { source: "snap-to-top" });
  }, [shouldSnapToTop, topPosition]);

  return (
    <BottomSheetComponent
      ref={sheetRef}
      open={Boolean(options.open || open)}
      onDismiss={setOpen ? onDismiss : undefined}
      onSpringEnd={onSpringEnd}
      onSpringStart={(e) => {
        // Simplified drag tracking for better performance
        const event = e as { source?: string }; // Type assertion for react-spring-bottom-sheet event
        if (
          event?.source !== "dragging" &&
          event?.source !== "snap-to-top" &&
          event?.source !== "snap-to-middle" &&
          event?.source !== "snap-to-bottom"
        )
          return;

        isDraggingRef.current = true;
        dragTrackingRef.current = sheetRef.current?.height || 0;
        const threshold = 10;

        // Throttled drag tracking to reduce performance impact
        let lastTrackTime = 0;
        const trackDrag = (currentTime: number) => {
          if (!isDraggingRef.current || !sheetRef.current) return;

          // Throttle to ~30fps instead of 60fps for better performance
          if (currentTime - lastTrackTime < 33) {
            requestAnimationFrame(trackDrag);
            return;
          }
          lastTrackTime = currentTime;

          const currentHeight = sheetRef.current.height;
          const previousHeight = dragTrackingRef.current;

          if (previousHeight !== null) {
            const heightDifference = currentHeight - previousHeight;

            if (Math.abs(heightDifference) > threshold) {
              const newDirection =
                heightDifference > 0 ? "draggingUp" : "draggingDown";

              if (lastDirectionRef.current !== newDirection) {
                onPositionChange?.(newDirection);
                lastDirectionRef.current = newDirection;
              }
            }
          }

          dragTrackingRef.current = currentHeight;
          requestAnimationFrame(trackDrag);
        };

        requestAnimationFrame(trackDrag);
      }}
      blocking={false}
      defaultSnap={useMemo(
        () => () => {
          switch (position) {
            case "top":
              return topPosition;
            case "middle":
              return middlePosition;
            default:
              return bottomPosition;
          }
        },
        [position, topPosition, middlePosition, bottomPosition],
      )}
      snapPoints={useMemo(
        () => () => [bottomPosition, middlePosition, topPosition],
        [bottomPosition, middlePosition, topPosition],
      )}
      expandOnContentDrag={false}
      style={useMemo(() => {
        const optionsWithRadius = options as {
          radius?: number;
          showHandle: boolean;
        };
        return {
          "--rsbs-overlay-rounded": optionsWithRadius.radius
            ? `${optionsWithRadius.radius}px`
            : position === "top"
              ? "0px"
              : "20px",
          "--rsbs-handle-bg":
            optionsWithRadius.showHandle === false
              ? "transparent"
              : "hsla(0, 0%, 0%, 0.14)",
          "--rsbs-header-draggable":
            optionsWithRadius.showHandle === false ? "none" : "all",
          zIndex: 0,
          "--rsbs-header-z-index": "1",
          "--rsbs-footer-z-index": "10",
        };
      }, [options, position])}
      header={
        withSpecialHeader ? (
          <div className="">
            {header}

            <div
              className={`h-48 absolute top-0 left-0 right-0  z-10 transition-opacity duration-200 ${currentPosition === "bottom" ? "visible" : "hidden"}`}
            />
          </div>
        ) : (
          header
        )
        // <div
        //   onClick={() => {
        //     setTimeout(() => {
        //       options?.setPosition("top");
        //       options?.setOpen("top");
        //     }, 0);
        //   }}
        // >
        //   {header}
        // </div>
      }
      footer={footer}
      scrollLocking={false}
    >
      {children}
    </BottomSheetComponent>
  );
};

// Main BottomSheet component that chooses implementation
const BottomSheet: FC<BottomSheetProps> = (props) => {
  if (props.lofiMode) {
    return (
      <LofiBottomSheet
        options={props.options}
        header={props.header}
        footer={props.footer}
        open={props.open}
        setOpen={props.setOpen}
        onPositionChange={props.onPositionChange}
        withSpecialHeader={props.withSpecialHeader}
      >
        {props.children}
      </LofiBottomSheet>
    );
  }
  return <FullBottomSheet {...props} />;
};

export default memo(BottomSheet);
